// eMAR overview — read-only PROJECTION of care-plan task fulfillment (CPOE D9):
// every medication dose task across the unit, with search + status filter.
// Doses are RECORDED through the task fulfillment dialog (patient → Tasks tab
// or My Tasks); this view renders those records. The legacy med_admin table is
// frozen — nothing writes it anymore, so this view can never disagree with the
// task record.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, Table, TableBody, TableCell, TableHead, TableRow, Chip, Button, TextField, MenuItem, InputAdornment, LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import { useNurseData } from "../NurseDataContext";
import { useDebouncedValue, useMyCoverage, coveredAdmissionIds } from "../hooks";
import { NURSE_COLORS } from "../theme";
import { todayStr } from "../dateNav";
import { getNurseTasks, listOf } from "../api/nurseApi";
import { outcomeOf, isGiveable, drugLabel, doseRouteLabel, isMedicationTask, sourceLabel } from "../medicationDisplay";
import MedicationAdministrationSheet from "../components/MedicationAdministrationSheet";


// Every selectable status the filter offers. Derivation itself lives in
// ../medicationDisplay so this view and the per-patient record cannot disagree
// about what happened to a dose.
const STATUS_OPTIONS = ["all", "due", "scheduled", "given", "withheld", "refused", "vomited", "cancelled", "missed"];

const hhmm = (dt) => (dt ? String(dt).replace("T", " ").slice(11, 16) : "");

export default function EmarOverview() {
  const { patients, navigate, dateFrom, currentNurseId } = useNurseData();
  const { myBedCodes } = useMyCoverage();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  // The round starts from what is owed, not from everything — a nurse opening
  // this screen mid-shift wants the doses to give, not the day's archive.
  const [status, setStatus] = useState("due");
  const [giving, setGiving] = useState(null);
  // Bumped after a dose is recorded, so the round refreshes in place.
  const [reloadKey, setReloadKey] = useState(0);
  // nurse_dashboard_tasks/ filters by a single date; default to today so the
  // unit-wide table stays bounded.
  const date = dateFrom || todayStr();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getNurseTasks({ date })
      .then((r) => { if (!cancelled) setTasks(listOf(r)); })
      .catch(() => { if (!cancelled) setTasks([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date, reloadKey]);

  const byAdmission = useMemo(() => {
    const map = {};
    patients.forEach((p) => { map[String(p.admissionId)] = p; });
    return map;
  }, [patients]);

  // Scope to the patients I cover this shift (roster ward ∪ assigned beds) —
  // the eMAR is "mine", not the whole unit. null coverage = still loading.
  const mine = useMemo(() => coveredAdmissionIds(patients, myBedCodes), [patients, myBedCodes]);
  const rows = useMemo(
    () => (mine ? tasks : [])
      .filter((t) => mine.has(String(t.admission_id)))
      .filter(isMedicationTask)
      .map((t) => ({ ...t, _outcome: outcomeOf(t), _patient: byAdmission[String(t.admission_id)] || t.patient || {} }))
      .sort((a, b) => String(a.scheduled_datetime).localeCompare(String(b.scheduled_datetime))),
    [tasks, byAdmission, mine]
  );

  const debouncedQ = useDebouncedValue(q);
  const filtered = useMemo(() => {
    const term = debouncedQ.toLowerCase().trim();
    return rows
      .filter((r) => status === "all" || r._outcome.key === status)
      .filter((r) => {
        if (!term) return true;
        return (r._patient.name || "").toLowerCase().includes(term) || drugLabel(r).toLowerCase().includes(term);
      });
  }, [rows, debouncedQ, status]);

  // The round, in the order a nurse walks it: everything owed right now first,
  // then each upcoming round in clock order. Doses with no round of their own
  // (hourly cycles, STAT, as-needed) collect at the end.
  const groups = useMemo(() => {
    const overdue = [];
    const byRound = new Map();
    const other = [];
    filtered.forEach((r) => {
      if (r._outcome.key === "due") { overdue.push(r); return; }
      const label = r.time_slot_label;
      if (!label) { other.push(r); return; }
      if (!byRound.has(label)) byRound.set(label, []);
      byRound.get(label).push(r);
    });
    const rounds = Array.from(byRound.entries())
      .map(([label, items]) => ({
        key: label,
        title: label,
        at: items.map((i) => hhmm(i.scheduled_datetime)).sort()[0],
        items,
      }))
      .sort((a, b) => String(a.at).localeCompare(String(b.at)));
    return [
      ...(overdue.length ? [{ key: "__overdue", title: "Due now", at: "", items: overdue, urgent: true }] : []),
      ...rounds,
      ...(other.length ? [{ key: "__other", title: "Unscheduled & timed", at: "", items: other }] : []),
    ];
  }, [filtered]);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">Medication Round</Typography>
        <Typography variant="body2" color="text.secondary">
          Every dose due across the patients you cover — give them here without opening each patient.
        </Typography>
      </Box>
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            size="small" placeholder="Search by patient or drug" value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
            sx={{ flex: 1 }}
          />
          <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            {STATUS_OPTIONS.map((s) => (<MenuItem key={s} value={s}>{s}</MenuItem>))}
          </TextField>
        </Stack>
      </Paper>
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Stack spacing={2}>
        {groups.map((g) => (
          <Paper key={g.key} sx={{ borderRadius: 2, border: `1px solid ${g.urgent ? NURSE_COLORS.primary : NURSE_COLORS.border}`, overflow: "hidden" }}>
            <Stack
              direction="row" spacing={1}
              sx={{ px: 2, py: 1, alignItems: "center", bgcolor: g.urgent ? "rgba(211,47,47,0.06)" : NURSE_COLORS.pageBg }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{g.title}</Typography>
              {g.at && <Typography variant="caption" color="text.secondary">{g.at}</Typography>}
              <Box sx={{ flex: 1 }} />
              <Chip size="small" variant="outlined" color={g.urgent ? "error" : "default"} label={g.items.length} />
            </Stack>
            <Box sx={{ overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
                    <TableCell>Bed</TableCell>
                    <TableCell>Patient</TableCell>
                    <TableCell>Drug</TableCell>
                    <TableCell>Dose / Route</TableCell>
                    <TableCell>Scheduled</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {g.items.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell sx={{ whiteSpace: "nowrap", fontWeight: 600 }}>
                        {r._patient?.bed || r._patient?.bed_code || "—"}
                      </TableCell>
                      <TableCell>{r._patient?.name || `Admission ${r.admission_id}`}</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{drugLabel(r)}</TableCell>
                      <TableCell>{doseRouteLabel(r)}</TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{hhmm(r.scheduled_datetime) || "—"}</TableCell>
                      <TableCell>
                        {/* No "meds not received" chip: nothing ever marks an order
                            Received, so it fired on every dose and distinguished
                            nothing. See D5 in the MAR spec. */}
                        <Chip size="small" label={r._outcome.label} color={r._outcome.color} variant={r._outcome.variant} />
                        {sourceLabel(r) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            from {sourceLabel(r)}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end" }}>
                          {isGiveable(r) && (
                            <Button size="small" variant="contained" color={g.urgent ? "warning" : "primary"} onClick={() => setGiving(r)}>
                              Give
                            </Button>
                          )}
                          <Button size="small" onClick={() => navigate("patient", { id: r._patient?.id || String(r.admission_id) })}>Open</Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        ))}
        {!loading && groups.length === 0 && (
          <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
            <Typography variant="body2" color="text.secondary">
              {status === "due" ? "Nothing due right now." : "No matching doses."}
            </Typography>
          </Paper>
        )}
      </Stack>

      <MedicationAdministrationSheet
        open={Boolean(giving)}
        task={giving}
        performedById={currentNurseId}
        onClose={() => setGiving(null)}
        onRecorded={() => setReloadKey((k) => k + 1)}
      />
    </Box>
  );
}
